(() => {
  const UNIT_FACTORS = { m: 1, cm: 0.01, mm: 0.001 };
  const UNIT_LABELS = { m: 'm', cm: 'cm', mm: 'mm' };
  const ELECTRIC_MAT_ALLOWANCE = 0.9;

  const parseArea = (value = '') => {
    const match = String(value).replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number.parseFloat(match[0]) : Number.NaN;
  };

  const formatArea = (value) => Number(value || 0).toFixed(2);

  class UheatRoomSizeCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.bindControls();
      this.hasCalculated = false;
      this.recommendedLabel = null;
      this.currentUnit = this.unitSelect?.value || 'm';
      this.rowSequence = 0;

      this.addEventListener('change', (event) => {
        event.stopPropagation();
        if (event.target.matches('input[type="radio"]') && this.hasCalculated) this.calculate();
      });
      this.addEventListener('input', (event) => {
        event.stopPropagation();
        this.clearFieldError(event.target);
        if (this.hasCalculated) this.calculate();
      });
      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-row]');
        if (removeButton) {
          const type = removeButton.dataset.removeRow;
          const row = removeButton.closest('.uheat-room-calculator__row');
          const adjacentRow = row?.nextElementSibling || row?.previousElementSibling;
          row?.remove();
          this.renumberRows(type);
          if (this.rowStatus) this.rowStatus.textContent = `${type === 'room' ? 'Room section' : 'Fixed area'} removed.`;
          const adjacentInput = adjacentRow?.querySelector('[data-length]');
          if (adjacentInput) adjacentInput.focus();
          else (type === 'room' ? this.addRoomButton : this.addExclusionButton)?.focus();
          if (this.hasCalculated) this.calculate();
          return;
        }

        const selectButton = event.target.closest('[data-select-size]');
        if (selectButton && this.recommendedLabel) this.selectProductSize(this.recommendedLabel);
      });

      this.addRow('room', false);
      if (this.dataset.mode === 'product' && this.dataset.simple !== 'true') this.addRow('exclusion', false);
      this.readProductOptions();
      this.updateAllowancePresentation();
    }

    refreshElements() {
      this.roomRows = this.querySelector('[data-room-rows]');
      this.exclusionRows = this.querySelector('[data-exclusion-rows]');
      this.addRoomButton = this.querySelector('[data-add-room]');
      this.addExclusionButton = this.querySelector('[data-add-exclusion]');
      this.calculateButton = this.querySelector('[data-calculate]');
      this.unitSelect = this.querySelector('[data-unit-select]');
      this.resultPanel = this.querySelector('[data-result-panel]');
      this.fixedSummaryError = this.querySelector('[data-fixed-summary-error]');
      this.rowStatus = this.querySelector('[data-row-status]');
      this.resetButton = this.querySelector('[data-reset-calculator]');
    }

    bindControls() {
      this.refreshElements();

      if (this.addRoomButton && this.addRoomButton.dataset.calculatorBound !== 'true') {
        this.addRoomButton.dataset.calculatorBound = 'true';
        this.addRoomButton.addEventListener('click', () => this.addRow('room'));
      }

      if (this.addExclusionButton && this.addExclusionButton.dataset.calculatorBound !== 'true') {
        this.addExclusionButton.dataset.calculatorBound = 'true';
        this.addExclusionButton.addEventListener('click', () => this.addRow('exclusion'));
      }

      if (this.calculateButton && this.calculateButton.dataset.calculatorBound !== 'true') {
        this.calculateButton.dataset.calculatorBound = 'true';
        this.calculateButton.addEventListener('click', () => {
          this.hasCalculated = true;
          this.calculate({ moveFocus: true });
        });
      }

      if (this.resetButton && this.resetButton.dataset.calculatorBound !== 'true') {
        this.resetButton.dataset.calculatorBound = 'true';
        this.resetButton.addEventListener('click', () => this.resetCalculator());
      }

      if (this.unitSelect && this.unitSelect.dataset.calculatorBound !== 'true') {
        this.unitSelect.dataset.calculatorBound = 'true';
        this.unitSelect.addEventListener('change', () => this.changeUnit());
      }
    }

    prepareForOpen() {
      this.bindControls();
      const hasRoomRow = Boolean(this.roomRows?.querySelector('[data-calculator-row="room"]'));
      if (this.hasCalculated || !hasRoomRow) this.resetCalculator({ focus: false });
    }

    get systemType() {
      return this.querySelector('input[type="radio"]:checked')?.value || this.dataset.defaultSystem || 'unsure';
    }

    get usesMatAllowance() {
      return this.systemType === 'electric-mat';
    }

    addRow(type, focus = true) {
      const container = type === 'room' ? this.roomRows : this.exclusionRows;
      if (!container) return;
      this.rowSequence += 1;
      const count = container.children.length + 1;
      const label = type === 'room' ? `Room section ${count}` : `Fixed area ${count}`;
      const rowId = `${this.id || 'uheat-calculator'}-${type}-${this.rowSequence}`;
      const row = document.createElement('div');
      row.className = 'uheat-room-calculator__row';
      row.dataset.calculatorRow = type;
      const nameField = this.dataset.mode === 'product' && this.dataset.simple !== 'true' ? `
        <div class="uheat-room-calculator__field uheat-room-calculator__row-name">
          <label for="${rowId}-name">Name</label>
          <input id="${rowId}-name" type="text" value="${label}" autocomplete="off">
        </div>` : '';
      row.innerHTML = `
        ${nameField}
        <div class="uheat-room-calculator__field">
          <label for="${rowId}-length">Length (<span data-unit-label>${UNIT_LABELS[this.currentUnit]}</span>)</label>
          <input id="${rowId}-length" type="number" min="0" step="any" inputmode="decimal" data-length aria-describedby="${rowId}-error">
        </div>
        <span class="uheat-room-calculator__multiply" aria-hidden="true">×</span>
        <div class="uheat-room-calculator__field">
          <label for="${rowId}-width">Width (<span data-unit-label>${UNIT_LABELS[this.currentUnit]}</span>)</label>
          <input id="${rowId}-width" type="number" min="0" step="any" inputmode="decimal" data-width aria-describedby="${rowId}-error">
        </div>
        <button class="uheat-room-calculator__remove" type="button" data-remove-row="${type}" aria-label="Remove ${label}">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"></path></svg>
          <span>Remove</span>
        </button>
        <p id="${rowId}-error" class="uheat-room-calculator__inline-error" data-row-error hidden></p>
      `;
      container.append(row);
      this.updateRemoveButtons(type);
      if (focus) {
        if (this.rowStatus) this.rowStatus.textContent = `${type === 'room' ? 'Room section' : 'Fixed area'} added.`;
        row.querySelector('[data-length]')?.focus();
      }
    }

    renumberRows(type) {
      const container = type === 'room' ? this.roomRows : this.exclusionRows;
      [...(container?.children || [])].forEach((row, index) => {
        const label = type === 'room' ? `Room section ${index + 1}` : `Fixed area ${index + 1}`;
        const name = row.querySelector('.uheat-room-calculator__row-name input');
        if (name && /^(Room section|Fixed area) \d+$/.test(name.value)) name.value = label;
        const button = row.querySelector('[data-remove-row]');
        if (button) button.setAttribute('aria-label', `Remove ${label}`);
      });
      this.updateRemoveButtons(type);
    }

    updateRemoveButtons(type) {
      const container = type === 'room' ? this.roomRows : this.exclusionRows;
      const rows = [...(container?.children || [])];
      rows.forEach((row) => {
        const button = row.querySelector('[data-remove-row]');
        if (!button) return;
        button.hidden = type === 'room' && rows.length === 1;
      });
    }

    changeUnit() {
      const nextUnit = this.unitSelect?.value || 'm';
      const previousFactor = UNIT_FACTORS[this.currentUnit] || 1;
      const nextFactor = UNIT_FACTORS[nextUnit] || 1;
      this.querySelectorAll('[data-length], [data-width]').forEach((input) => {
        const value = Number.parseFloat(input.value);
        if (Number.isFinite(value)) input.value = String(Number((value * previousFactor / nextFactor).toFixed(3)));
      });
      this.currentUnit = nextUnit;
      this.querySelectorAll('[data-unit-label]').forEach((label) => { label.textContent = UNIT_LABELS[nextUnit]; });
      if (this.hasCalculated) this.calculate();
    }

    readProductOptions() {
      this.productOptions = [];
      const data = this.querySelector('[data-area-options]');
      if (!data) return;
      try {
        this.productOptions = JSON.parse(data.textContent || '[]')
          .map((option) => ({ ...option, area: parseArea(option.label) }))
          .filter((option) => Number.isFinite(option.area))
          .sort((a, b) => a.area - b.area);
      } catch (error) {
        console.warn('[Uheat room calculator] Could not read product sizes.', error);
      }
    }

    clearFieldError(target) {
      const row = target.closest?.('.uheat-room-calculator__row');
      if (!row) return;
      row.classList.remove('has-error');
      row.querySelectorAll('input').forEach((input) => input.removeAttribute('aria-invalid'));
      const error = row.querySelector('[data-row-error]');
      if (error) error.hidden = true;
    }

    setRowError(row, copy) {
      row.classList.add('has-error');
      row.querySelectorAll('[data-length], [data-width]').forEach((input) => input.setAttribute('aria-invalid', 'true'));
      const error = row.querySelector('[data-row-error]');
      if (error) {
        error.textContent = copy;
        error.hidden = false;
      }
    }

    validateRows(type) {
      const rows = [...this.querySelectorAll(`[data-calculator-row="${type}"]`)];
      const factor = UNIT_FACTORS[this.currentUnit] || 1;
      let total = 0;
      let valid = true;
      rows.forEach((row) => {
        this.clearFieldError(row.querySelector('input'));
        const lengthInput = row.querySelector('[data-length]');
        const widthInput = row.querySelector('[data-width]');
        const lengthRaw = lengthInput?.value.trim() || '';
        const widthRaw = widthInput?.value.trim() || '';
        const optionalBlank = type === 'exclusion' && lengthRaw === '' && widthRaw === '';
        if (optionalBlank) return;

        const length = Number.parseFloat(lengthRaw);
        const width = Number.parseFloat(widthRaw);
        if (!Number.isFinite(length) || !Number.isFinite(width)) {
          this.setRowError(row, 'Enter both the length and width.');
          valid = false;
          return;
        }
        if (length <= 0 || width <= 0) {
          this.setRowError(row, 'Measurements must be greater than zero.');
          valid = false;
          return;
        }
        const lengthMetres = length * factor;
        const widthMetres = width * factor;
        if (lengthMetres > 100 || widthMetres > 100) {
          this.setRowError(row, 'This measurement is over 100 metres. Check the value or selected unit.');
          valid = false;
          return;
        }
        total += lengthMetres * widthMetres;
      });
      return { total, valid };
    }

    calculate({ moveFocus = false } = {}) {
      const rooms = this.validateRows('room');
      const exclusions = this.validateRows('exclusion');
      const roomArea = rooms.total;
      const exclusionArea = exclusions.total;
      const usableArea = Math.max(0, roomArea - exclusionArea);
      const appliesFittingAllowance = this.dataset.mode !== 'product' || this.usesMatAllowance;
      const planningArea = appliesFittingAllowance ? usableArea * ELECTRIC_MAT_ALLOWANCE : usableArea;

      this.setAreaValue('measured', roomArea);
      this.setAreaValue('fixed', exclusionArea);
      this.setAreaValue('usable', usableArea);
      this.setAreaValue('planning', planningArea);
      this.updateAllowancePresentation();
      this.clearFixedSummaryError();

      if (!rooms.valid || !exclusions.valid) {
        this.setMessage('Check the highlighted measurements before calculating.', 'warning');
        this.hideRecommendation();
        this.hideResultActions();
        return;
      }

      if (roomArea <= 0) {
        this.setMessage('Enter at least one room length and width.', 'warning');
        this.hideRecommendation();
        this.hideResultActions();
        return;
      }

      if (exclusionArea >= roomArea) {
        this.setFixedSummaryError('Fixed areas must be smaller than the total measured room area.');
        this.setMessage('Check the fixed-area measurements before calculating.', 'warning');
        this.hideRecommendation();
        this.hideResultActions();
        return;
      }

      if (this.dataset.mode !== 'product') {
        const description = exclusionArea > 0
          ? `Based on ${formatArea(roomArea)} m² measured minus ${formatArea(exclusionArea)} m² of fixed areas.`
          : `Based on ${formatArea(usableArea)} m² of usable floor area.`;
        this.setMessage(description, 'success');
        this.hideRecommendation();
        this.showResultActions(planningArea);
      } else {
        this.recommendProductSize(planningArea);
      }

      if (moveFocus) this.resultPanel?.focus({ preventScroll: false });
    }

    updateAllowancePresentation() {
      const row = this.querySelector('[data-allowance-row]');
      const usableRow = this.querySelector('[data-usable-area]')?.closest('div');
      if (!row || !usableRow) return;
      row.hidden = !this.usesMatAllowance;
      usableRow.classList.toggle('is-final', !this.usesMatAllowance);
    }

    setAreaValue(key, value) {
      const target = this.querySelector(`[data-${key}-area]`);
      if (target) target.textContent = formatArea(value);
    }

    showResultActions(area) {
      const actions = this.querySelector('[data-result-actions]');
      const link = this.querySelector('[data-find-systems]');
      const label = this.querySelector('[data-find-systems-label]');
      if (!actions || !link || !label) return;
      const url = new URL(this.dataset.selectorUrl || '/pages/system-selection-tool', window.location.origin);
      url.searchParams.set('heated_area', formatArea(area));
      url.searchParams.set('unit', 'm2');
      url.searchParams.set('system_type', this.systemType);
      url.searchParams.set('area_source', 'room-calculator');
      link.href = `${url.pathname}${url.search}`;
      label.textContent = 'Find suitable systems';
      actions.hidden = false;
    }

    hideResultActions() {
      const actions = this.querySelector('[data-result-actions]');
      if (actions) actions.hidden = true;
    }

    resetCalculator({ focus = true } = {}) {
      this.refreshElements();
      this.roomRows.replaceChildren();
      this.exclusionRows.replaceChildren();
      this.addRow('room', false);
      if (this.dataset.mode === 'product' && this.dataset.simple !== 'true') this.addRow('exclusion', false);
      this.hasCalculated = false;
      this.recommendedLabel = null;
      this.clearFixedSummaryError();
      this.hideRecommendation();
      this.hideResultActions();
      ['measured', 'fixed', 'usable', 'planning'].forEach((key) => {
        const target = this.querySelector(`[data-${key}-area]`);
        if (target) target.textContent = '0.00';
      });
      this.setMessage('Enter your measurements to calculate the heated area.');
      if (focus) this.querySelector('[data-length]')?.focus();
    }

    setFixedSummaryError(copy) {
      if (!this.fixedSummaryError) return;
      this.fixedSummaryError.textContent = copy;
      this.fixedSummaryError.hidden = false;
    }

    clearFixedSummaryError() {
      if (this.fixedSummaryError) this.fixedSummaryError.hidden = true;
    }

    recommendProductSize(planningArea) {
      if (!this.productOptions.length) {
        this.hideRecommendation();
        this.setMessage('Your area is calculated, but this product does not have readable area-based sizes. Please choose manually or ask our team.', 'warning');
        return;
      }

      const minimum = this.productOptions[0];
      const maximum = this.productOptions[this.productOptions.length - 1];
      const areaName = this.usesMatAllowance ? 'electric-mat planning figure' : 'usable heated area';
      if (planningArea < minimum.area) {
        this.hideRecommendation();
        this.setMessage(`The ${areaName} is below the smallest ${minimum.label} option. Ask our team to confirm the correct approach.`, 'warning');
        return;
      }

      if (planningArea > maximum.area) {
        this.hideRecommendation();
        this.setMessage(
          this.usesMatAllowance
            ? `The ${areaName} is larger than the biggest single ${maximum.label} option. You may need more than one mat.`
            : `The ${areaName} is larger than the biggest single ${maximum.label} option. Ask our team to confirm how the system should be divided.`,
          'warning'
        );
        return;
      }

      const recommendation = [...this.productOptions].reverse().find((option) => option.area <= planningArea);
      if (!recommendation) {
        this.hideRecommendation();
        this.setMessage('No suitable single size was found. Please ask our team to confirm the correct system.', 'warning');
        return;
      }

      this.showRecommendation(recommendation);
      this.setMessage(
        recommendation.available
          ? (this.usesMatAllowance
              ? 'This is the largest single size that stays within the electric-mat planning allowance.'
              : 'This is the closest available single size within the usable heated area.')
          : `${recommendation.label} is the calculated match but is currently unavailable.`,
        recommendation.available ? 'success' : 'warning'
      );
    }

    showRecommendation(option) {
      const box = this.querySelector('[data-recommendation]');
      const button = this.querySelector('[data-select-size]');
      this.recommendedLabel = option.label;
      box.hidden = false;
      this.querySelector('[data-recommendation-size]').textContent = option.label;
      this.querySelector('[data-recommendation-copy]').textContent = option.available
        ? 'A practical starting point for this product based on your measured usable area.'
        : 'This calculated size is currently unavailable.';
      button.hidden = !option.available;
    }

    hideRecommendation() {
      this.recommendedLabel = null;
      const box = this.querySelector('[data-recommendation]');
      const button = this.querySelector('[data-select-size]');
      if (box) box.hidden = true;
      if (button) button.hidden = true;
    }

    setMessage(copy, state = '') {
      const message = this.querySelector('[data-calculator-message]');
      if (!message) return;
      message.textContent = copy;
      if (state) message.dataset.state = state;
      else delete message.dataset.state;
    }

    selectProductSize(label) {
      const dialog = this.closest('dialog');
      const picker = this.closest('variant-picker')
        || dialog?.previousElementSibling?.closest('.variant-picker__form')?.closest('variant-picker')
        || document.querySelector(`variant-picker[data-product-id="${this.closest('[data-product-id]')?.dataset.productId || ''}"]`)
        || document.querySelector('main variant-picker[data-template-product-match="true"]');
      if (!picker) {
        this.setMessage('We could not select the option automatically. Close the calculator and choose the recommended size manually.', 'warning');
        return;
      }

      const optionName = this.dataset.areaOptionName;
      const controls = [...picker.querySelectorAll('input[data-option-name], option[data-option-name]')];
      const control = controls.find((item) => item.dataset.optionName === optionName && item.value === label);
      if (!control) {
        this.setMessage('We could not select the option automatically. Choose the recommended size manually.', 'warning');
        return;
      }

      if (control instanceof HTMLInputElement) {
        control.checked = true;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (control instanceof HTMLOptionElement) {
        control.selected = true;
        control.closest('select')?.dispatchEvent(new Event('change', { bubbles: true }));
      }
      dialog?.close();
      const inlinePanel = this.closest('[data-uheat-calculator-panel]');
      const inlineShell = inlinePanel?.closest('[data-uheat-calculator-shell]');
      const inlineToggle = inlineShell?.querySelector('[data-uheat-calculator-toggle]');
      if (inlinePanel && inlineToggle) {
        inlinePanel.hidden = true;
        inlineToggle.setAttribute('aria-expanded', 'false');
        inlineToggle.querySelector('[data-uheat-calculator-toggle-label]').textContent = inlineToggle.dataset.closedLabel;
      }
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  if (!customElements.get('uheat-room-size-calculator')) {
    customElements.define('uheat-room-size-calculator', UheatRoomSizeCalculator);
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-uheat-calculator-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.dataset.uheatCalculatorToggle);
    if (!panel) return;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    panel.hidden = isOpen;
    toggle.setAttribute('aria-expanded', String(!isOpen));
    const label = toggle.querySelector('[data-uheat-calculator-toggle-label]');
    if (label) label.textContent = isOpen ? toggle.dataset.closedLabel : 'Hide calculator';
    if (!isOpen) {
      panel.querySelector('uheat-room-size-calculator')?.prepareForOpen();
      panel.querySelector('[data-length]')?.focus({ preventScroll: true });
    }
  });
})();
